import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Typography, Chip, Box, IconButton, Tooltip, TextField, Dialog, DialogTitle, DialogContent, DialogActions, Button, Menu, MenuItem } from '@mui/material';
import { ApiTypes, AiLlmTypes } from '@flashbacktech/flashbackclient/api';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import eyeSvg from '../assets/details-bucket/eye.svg';
import settingsSvg from '../assets/details-bucket/settings.svg';
import deleteSvg from '../assets/details-bucket/delete.svg';
import anthropicLogo from '../assets/cloud-providers/anthropic-logo.svg';
import googleCloudLogo from '../assets/cloud-providers/google-cloud-icon.svg';
import awsLogo from '../assets/cloud-providers/aws.svg';
import { useAuth, useSelectedWorkspace, useWorkspaces } from '../contexts/AuthContext';
import { useApi } from '../contexts/ApiContext';
import { useWithTokenRefresh } from '../utils/api';
import { buildBreakdownParams } from '../utils/v2StatsHelpers';
import { toastError, toastSuccess } from '../utils/toast';
import GuidedHighlight from '../components/GuidedHighlight';

// Guided highlight steps for AI LLM page
const aiLlmGuideSteps = [
  {
    id: 'add-ai-provider',
    targetSelector: '[data-tutorial="add-ai"]',
    title: 'Add AI Provider',
    description: 'Click here to configure a new AI provider like OpenAI, Anthropic, or Google.',
    position: 'bottom' as const,
  },
];

function AILLM() {
  const navigate = useNavigate();
  const location = useLocation();
  const { globalData, authState } = useAuth();
  const { apiClient } = useApi();
  const withTokenRefresh = useWithTokenRefresh();
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [showProviderSelection, setShowProviderSelection] = useState(false);
  
  // Guided highlight state
  const [showGuide, setShowGuide] = useState(false);
  const [guideStep, setGuideStep] = useState(0);
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState('');
  const [selectedWorkspace, setSelectedWorkspace] = useState('');
  const [aiLlms, setAiLlms] = useState<AiLlmTypes.AiLlmDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [llmStats, setLlmStats] = useState<Record<string, { totalApiCalls: string; totalTokensIn: string; totalTokensOut: string }>>({});
  const [configName, setConfigName] = useState('');
  const [endpoint, setEndpoint] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [apiSecret, setApiSecret] = useState('');
  const [editingAiLlmId, setEditingAiLlmId] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [aiLlmToDelete, setAiLlmToDelete] = useState<AiLlmTypes.AiLlmDTO | null>(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuConfigId, setMenuConfigId] = useState<string | null>(null);
  
  const [availableApiKeys, setAvailableApiKeys] = useState<any[]>([]);
  const [isLoadingApiKeys, setIsLoadingApiKeys] = useState(false);
  const [apiKeyUuid, setApiKeyUuid] = useState<string | undefined>(undefined);
  
  // Use centralized workspace hook
  const selectedWorkspaceId = useSelectedWorkspace();
  const workspaces = useWorkspaces();

  // Load available API keys (when editing, use the config's workspace so we get keys for that workspace)
  const loadApiKeys = async () => {
    try {
      setIsLoadingApiKeys(true);
      const editingConfig = editingAiLlmId ? aiLlms.find((a) => a.id === editingAiLlmId) : null;
      const targetWorkspaceId = editingConfig?.workspaceId
        ?? (selectedWorkspaceId !== 'ALL' ? selectedWorkspaceId : undefined);
      const response = await apiClient.getApiKeys({
        workspaceId: targetWorkspaceId,
        capability: ApiTypes.ProviderCapability.AI,
        ...(selectedProvider ? { provider: selectedProvider } : {}),
      });
      if (response && response.success) {
        setAvailableApiKeys(response.apiKeys || []);
      }
    } catch (error) {
      console.error('Failed to load API keys:', error);
    } finally {
      setIsLoadingApiKeys(false);
    }
  };

  useEffect(() => {
    if (showProviderSelection || showAddForm) {
      loadApiKeys();
    }
  }, [showProviderSelection, showAddForm, selectedWorkspaceId, selectedProvider, editingAiLlmId, aiLlms]);

  // Load AI LLMs and stats
  useEffect(() => {
    loadAILlms();
    loadAllLlmStats();
  }, [selectedWorkspaceId]);

  // Open Add AI LLM flow when triggered from another page
  useEffect(() => {
    try {
      const flag = localStorage.getItem('triggerAddAILLM');
      if (flag === '1') {
        localStorage.removeItem('triggerAddAILLM');
        setShowProviderSelection(true);
      }
    } catch (e) { /* ignore */ }
  }, []);

  // Handle edit mode or add trigger from navigation state
  useEffect(() => {
    const state = location.state as { editId?: string; editProvider?: string; triggerAdd?: boolean } | null;
    if (state?.triggerAdd) {
      setShowProviderSelection(true);
      navigate(location.pathname, { replace: true, state: null });
    } else if (state?.editId && aiLlms.length > 0) {
      const config = aiLlms.find(c => c.id === state.editId);
      if (config) {
        setEditingAiLlmId(state.editId);
        setSelectedProvider(config.aiType || state.editProvider || '');
        setShowProviderSelection(false);
        setShowAddForm(true);
        // Clear navigation state to prevent re-triggering
        navigate(location.pathname, { replace: true, state: null });
      }
    }
  }, [location.state, aiLlms, navigate, location.pathname]);
  
  // Check if guided tutorial should be shown
  useEffect(() => {
    // Show guide if user has no AI configs and hasn't dismissed the guide
    // BUT NOT if the global CloudGatewayTutorial is already running
    const checkGuide = () => {
      try {
        const guideDismissed = localStorage.getItem('aiLlmGuideDismissed');
        const guideCompleted = localStorage.getItem('aiLlmGuideCompleted');
        const globalTutorialStep = localStorage.getItem('gatewayTutorialStep');
        const globalTutorialCompleted = localStorage.getItem('gatewayTutorialCompleted');
        
        // Don't show local guide if global tutorial is active (step 0 = add-ai step)
        const isGlobalTutorialActive = globalTutorialStep !== null && globalTutorialCompleted !== 'true';
        
        // Show guide for new users (no AI configs) who haven't dismissed/completed it
        // and global tutorial is not already guiding them
        if (!guideDismissed && !guideCompleted && !loading && aiLlms.length === 0 && !isGlobalTutorialActive) {
          // Small delay to let the UI render
          setTimeout(() => setShowGuide(true), 500);
        }
      } catch { /* ignore */ }
    };
    
    checkGuide();
  }, [loading, aiLlms.length]);
  
  // Also check for URL param to trigger guide
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('guide') === 'true') {
      setShowGuide(true);
      // Remove the param from URL
      params.delete('guide');
      const newUrl = params.toString() ? `${location.pathname}?${params.toString()}` : location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, [location]);
  
  const handleGuideComplete = () => {
    setShowGuide(false);
    try {
      localStorage.setItem('aiLlmGuideCompleted', 'true');
    } catch { /* ignore */ }
  };
  
  const handleGuideDismiss = () => {
    setShowGuide(false);
    try {
      localStorage.setItem('aiLlmGuideDismissed', 'true');
    } catch { /* ignore */ }
  };

  const loadAILlms = async () => {
    if (!authState?.token) return;
    
    setLoading(true);
    try {
      const workspaceId = selectedWorkspaceId && selectedWorkspaceId !== 'ALL' ? selectedWorkspaceId : undefined;
      const response = await withTokenRefresh(() => apiClient.getAiLlms(workspaceId));
      
      if (response.success && response.aiLlms) {
        setAiLlms(response.aiLlms);
      } else {
        console.error('[AILLM] Failed to load AI LLMs:', response);
        setAiLlms([]);
      }
    } catch (error) {
      console.error('[AILLM] Error loading AI LLMs:', error);
      toastError('Failed to load AI LLM configurations');
      setAiLlms([]);
    } finally {
      setLoading(false);
    }
  };

  const loadAllLlmStats = async () => {
    if (!authState?.token) return;
    try {
      const wsId = selectedWorkspaceId && selectedWorkspaceId !== 'ALL' ? selectedWorkspaceId : null;
      const bdParams = buildBreakdownParams(wsId, null, '30d');
      const response = await withTokenRefresh(() =>
        apiClient.getAiGatewayBreakdown('models', bdParams as any)
      ) as any;
      const statsMap: Record<string, { totalApiCalls: string; totalTokensIn: string; totalTokensOut: string }> = {};
      if (response?.success && response?.entities) {
        response.entities.forEach((entity: any) => {
          statsMap[entity.entityId] = {
            totalApiCalls: String(entity.totals?.requests || 0),
            totalTokensIn: String(entity.totals?.tokensIn || 0),
            totalTokensOut: String(entity.totals?.tokensOut || 0),
          };
        });
      }
      setLlmStats(statsMap);
    } catch (error) {
      console.error('[AILLM] Error loading LLM stats:', error);
    }
  };

  // Pre-select current workspace when form opens and reset form state
  useEffect(() => {
    if (showAddForm) {
      // Use centralized selectedWorkspaceId from hook
      if (selectedWorkspaceId && selectedWorkspaceId !== 'ALL') {
        setSelectedWorkspace(selectedWorkspaceId);
      } else if (workspaces && workspaces.length > 0) {
        setSelectedWorkspace(workspaces[0].id);
      }
      
      // If editing, load the AI LLM data
      if (editingAiLlmId) {
        const aiLlm = aiLlms.find(a => a.id === editingAiLlmId);
        if (aiLlm) {
          setConfigName(aiLlm.name);
          setEndpoint(aiLlm.endpoint);
          setApiSecret(''); // Don't populate secret for security
          setSelectedProvider(aiLlm.aiType);
          setSelectedWorkspace(aiLlm.workspaceId);
          setApiKeyUuid((aiLlm as any).apiKeyId);
        }
      } else {
        // Set default endpoint for new entries
        setEndpoint('');
        setApiKeyUuid(undefined);
      }
    } else {
      // Reset form state when form is closed
      setConfigName('');
      setEndpoint('');
      setApiKey('');
      setApiSecret('');
      setEditingAiLlmId(null);
      setApiKeyUuid(undefined);
    }
  }, [showAddForm, globalData.workspaces, selectedProvider, editingAiLlmId, aiLlms]);

  const filteredConfigs = aiLlms.filter((config) => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      config.name.toLowerCase().includes(query) ||
      config.aiType.toLowerCase().includes(query) ||
      config.endpoint.toLowerCase().includes(query)
    );
  });

  const getAITypeColor = (aiType: string) => {
    switch (aiType) {
      case 'OPENAI':
        return { bg: '#EAF8F1', color: '#1F8F6B', border: '#D1FAE5' };
      case 'GOOGLE':
        return { bg: '#F0F2FF', color: '#617BFF', border: '#D6DDFF' };
      case 'ANTHROPIC':
        return { bg: '#FFF7E6', color: '#C38A00', border: '#FEF3C7' };
      case 'AWS':
        return { bg: '#FEF2F2', color: '#DC2626', border: '#FEE2E2' };
      default:
        return { bg: '#F3F4F6', color: '#6B7280', border: '#E5E7EB' };
    }
  };

  const getProviderIcon = (aiType: string): JSX.Element => {
    switch (aiType.toUpperCase()) {
      case 'OPENAI':
        return (
          <svg viewBox="0 0 24 24" style={{ width: '18px', height: '18px' }} fill="#10A37F">
            <path d="M22.282 9.821a5.985 5.985 0 0 0-.516-4.91 6.046 6.046 0 0 0-6.51-2.9A6.065 6.065 0 0 0 4.981 4.18a5.985 5.985 0 0 0-3.998 2.9 6.046 6.046 0 0 0 .743 7.097 5.98 5.98 0 0 0 .51 4.911 6.051 6.051 0 0 0 6.515 2.9A5.985 5.985 0 0 0 13.26 24a6.056 6.056 0 0 0 5.772-4.206 5.99 5.99 0 0 0 3.997-2.9 6.056 6.056 0 0 0-.747-7.073zM13.26 22.43a4.476 4.476 0 0 1-2.876-1.04l.141-.081 4.779-2.758a.795.795 0 0 0 .392-.681v-6.737l2.02 1.168a.071.071 0 0 1 .038.052v5.583a4.504 4.504 0 0 1-4.494 4.494zM3.6 18.304a4.47 4.47 0 0 1-.535-3.014l.142.085 4.783 2.759a.771.771 0 0 0 .78 0l5.843-3.369v2.332a.08.08 0 0 1-.033.062L9.74 19.95a4.5 4.5 0 0 1-6.14-1.646zM2.34 7.896a4.485 4.485 0 0 1 2.366-1.973V11.6a.766.766 0 0 0 .388.676l5.815 3.355-2.02 1.168a.076.076 0 0 1-.071 0l-4.83-2.786A4.504 4.504 0 0 1 2.34 7.872zm16.597 3.855l-5.833-3.387L15.119 7.2a.076.076 0 0 1 .071 0l4.83 2.791a4.494 4.494 0 0 1-.676 8.105v-5.678a.79.79 0 0 0-.407-.667zm2.01-3.023l-.141-.085-4.774-2.782a.776.776 0 0 0-.785 0L9.409 9.23V6.897a.066.066 0 0 1 .028-.061l4.83-2.787a4.5 4.5 0 0 1 6.68 4.66zm-12.64 4.135l-2.02-1.164a.08.08 0 0 1-.038-.057V6.075a4.5 4.5 0 0 1 7.375-3.453l-.142.08L8.704 5.46a.795.795 0 0 0-.393.681zm1.097-2.365l2.602-1.5 2.607 1.5v2.999l-2.597 1.5-2.607-1.5z"/>
          </svg>
        );
      case 'ANTHROPIC':
        return <img src={anthropicLogo} alt="Anthropic" style={{ width: '18px', height: '18px' }} />;
      case 'GEMINI':
      case 'GOOGLE':
        return <img src={googleCloudLogo} alt="Google" style={{ width: '18px', height: '18px' }} />;
      case 'BEDROCK':
      case 'AWS':
        return <img src={awsLogo} alt="AWS" style={{ width: '18px', height: '18px' }} />;
      default:
        return (
          <span style={{ fontSize: '10px', fontWeight: 700, color: '#8B5CF6' }}>
            AI
          </span>
        );
    }
  };

  // Handle menu open/close
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, configId: string) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setMenuConfigId(configId);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuConfigId(null);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: '#FFFFFF' }}>
      {/* Header */}
      <div style={{ padding: '24px 40px 24px 40px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <Typography
            style={{
              fontSize: '32px',
              fontWeight: 700,
              color: '#1F2937',
              marginBottom: '8px',
              fontFamily: 'Inter',
              letterSpacing: '-0.02em',
            }}
          >
            AI
          </Typography>
          <Typography
            style={{
              fontSize: '16px',
              fontWeight: 400,
              color: '#6B7280',
              fontFamily: 'Inter',
              lineHeight: '24px',
            }}
          >
            Configure and manage your AI integrations and language models
          </Typography>
          {(showProviderSelection || showAddForm) && (
            <Button
              variant="text"
              startIcon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>}
              onClick={() => {
                if (showAddForm) {
                  setConfigName('');
                  setEndpoint('');
                  setApiKey('');
                  setApiSecret('');
                  setEditingAiLlmId(null);
                  setShowAddForm(false);
                  setShowProviderSelection(true);
                } else if (showProviderSelection) {
                  setShowProviderSelection(false);
                }
              }}
              sx={{ mt: 2, color: '#617BFF', textTransform: 'none', fontWeight: 500, border: '1px solid #617BFF', borderRadius: '8px', px: 2, '&:hover': { bgcolor: '#F5F7FF' } }}
            >
              Back to LLM
            </Button>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div style={{ padding: '0 40px 40px 40px', flex: 1, display: 'flex', flexDirection: 'column' }}>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', backgroundColor: '#FFFFFF' }}>
          <div className="flex-1 overflow-y-auto p-6">

      {/* LLM Sub-section Header with buttons */}
      {!showProviderSelection && !showAddForm && (
        <Box>
          {/* Connection Status Summary */}
          
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3 }}>
            <Box>
              <Typography sx={{ fontWeight: 600, fontSize: '18px', color: '#101828', mb: 0.5 }}>
                LLM
              </Typography>
              <Typography sx={{ fontSize: '14px', color: '#667085' }}>
                Manage your AI Language Model provider configurations.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {/* Search */}
              {showSearch ? (
                <TextField
                  size="small"
                  placeholder="Search configurations..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onBlur={() => { if (!searchQuery) setShowSearch(false); }}
                  autoFocus
                  sx={{ width: 200, '& .MuiOutlinedInput-root': { borderRadius: '8px', height: '36px' } }}
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ fontSize: 18, color: '#667085', mr: 0.5 }} />,
                  }}
                />
              ) : (
                <Tooltip title="Search">
                  <IconButton
                    size="small"
                    onClick={() => setShowSearch(true)}
                    sx={{ width: 36, height: 36, border: '1px solid #D0D5DD', borderRadius: '8px', color: '#667085', '&:hover': { bgcolor: '#F9FAFB', borderColor: '#98A2B3' } }}
                  >
                    <SearchIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              )}
              {/* Reload */}
              <Tooltip title="Reload">
                <IconButton
                  size="small"
                  onClick={() => loadAILlms()}
                  disabled={loading}
                  sx={{ width: 36, height: 36, border: '1px solid #D0D5DD', borderRadius: '8px', color: '#667085', '&:hover': { bgcolor: '#F9FAFB', borderColor: '#98A2B3' } }}
                >
                  <RefreshIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
              {/* Add */}
              <Tooltip title="Add configuration">
                <IconButton
                  size="small"
                  onClick={() => setShowProviderSelection(true)}
                  data-tutorial="add-ai"
                  sx={{ width: 36, height: 36, bgcolor: '#617BFF', borderRadius: '8px', color: 'white', '&:hover': { bgcolor: '#4A67E3' } }}
                >
                  <AddIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>
      )}

      {showProviderSelection ? (
        /* Provider Selection */
        <div className="mt-4">
          <Typography
            style={{
              fontWeight: 600,
              fontSize: '18px',
              color: '#101828',
              marginBottom: '4px',
            }}
          >
            Add LLM
          </Typography>
          <Typography
            style={{
              fontSize: '14px',
              color: '#667085',
              marginBottom: '24px',
            }}
          >
            Select a provider and configure your AI language model integration.
          </Typography>
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <div className="flex justify-between items-center mb-6">
              <Box>
                <Typography
                  variant="h5"
                  className="text-black"
                  style={{ fontWeight: 600, fontSize: '20px', lineHeight: '30px' }}
                >
                  Select Provider
                </Typography>
                <Typography
                  variant="body1"
                  style={{
                    color: '#535862',
                    fontWeight: 400,
                    fontSize: '14px',
                    lineHeight: '20px',
                    marginTop: '4px',
                  }}
                >
                  Choose your AI provider to get started.
                </Typography>
              </Box>
            </div>
            <div className={`grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6 flex-1`} data-tutorial="provider-selection">
              {/* OpenAI Provider */}
              <div
                className="border border-gray-200 hover:border-green-500 transition-all rounded-lg cursor-pointer"
                data-tutorial="provider-openai"
                style={{
                  width: '100%',
                  height: 205,
                  borderRadius: 12,
                  display: 'flex',
                  flexDirection: 'column',
                }}
                onClick={() => {
                  setSelectedProvider('OPENAI');
                  setShowProviderSelection(false);
                  setShowAddForm(true);
                }}
              >
                <div className="flex-1 p-4">
                  <div className="flex items-center mb-3">
                    <div
                      style={{
                        width: 48,
                        height: 48,
                        borderRadius: 8,
                        backgroundColor: '#E6F4F1',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginRight: 12,
                      }}
                    >
                      <svg viewBox="0 0 24 24" style={{ width: '28px', height: '28px' }} fill="currentColor">
                        <path d="M22.282 9.821a5.985 5.985 0 0 0-.516-4.91 6.046 6.046 0 0 0-6.51-2.9A6.065 6.065 0 0 0 4.981 4.18a5.985 5.985 0 0 0-3.998 2.9 6.046 6.046 0 0 0 .743 7.097 5.98 5.98 0 0 0 .51 4.911 6.051 6.051 0 0 0 6.515 2.9A5.985 5.985 0 0 0 13.26 24a6.056 6.056 0 0 0 5.772-4.206 5.99 5.99 0 0 0 3.997-2.9 6.056 6.056 0 0 0-.747-7.073zM13.26 22.43a4.476 4.476 0 0 1-2.876-1.04l.141-.081 4.779-2.758a.795.795 0 0 0 .392-.681v-6.737l2.02 1.168a.071.071 0 0 1 .038.052v5.583a4.504 4.504 0 0 1-4.494 4.494zM3.6 18.304a4.47 4.47 0 0 1-.535-3.014l.142.085 4.783 2.759a.771.771 0 0 0 .78 0l5.843-3.369v2.332a.08.08 0 0 1-.033.062L9.74 19.95a4.5 4.5 0 0 1-6.14-1.646zM2.34 7.896a4.485 4.485 0 0 1 2.366-1.973V11.6a.766.766 0 0 0 .388.676l5.815 3.355-2.02 1.168a.076.076 0 0 1-.071 0l-4.83-2.786A4.504 4.504 0 0 1 2.34 7.872zm16.597 3.855l-5.833-3.387L15.119 7.2a.076.076 0 0 1 .071 0l4.83 2.791a4.494 4.494 0 0 1-.676 8.105v-5.678a.79.79 0 0 0-.407-.667zm2.01-3.023l-.141-.085-4.774-2.782a.776.776 0 0 0-.785 0L9.409 9.23V6.897a.066.066 0 0 1 .028-.061l4.83-2.787a4.5 4.5 0 0 1 6.68 4.66zm-12.64 4.135l-2.02-1.164a.08.08 0 0 1-.038-.057V6.075a4.5 4.5 0 0 1 7.375-3.453l-.142.08L8.704 5.46a.795.795 0 0 0-.393.681zm1.097-2.365l2.602-1.5 2.607 1.5v2.999l-2.597 1.5-2.607-1.5z" fill="#10A37F"/>
                      </svg>
                    </div>
                    <span
                      style={{
                        fontWeight: 600,
                        fontSize: 16,
                        lineHeight: '24px',
                        color: '#181D27',
                      }}
                    >
                      OpenAI
                    </span>
                  </div>
                  <p
                    style={{
                      fontWeight: 400,
                      fontSize: 14,
                      lineHeight: '20px',
                      color: '#535862',
                    }}
                  >
                    Connect to OpenAI services including GPT-4, GPT-3.5 and other models.
                  </p>
                </div>
                <div
                  className="flex items-center justify-end border-t px-4"
                  style={{ height: 53 }}
                >
                  <button
                    className="hover:text-blue-800"
                    style={{
                      fontWeight: 600,
                      fontSize: 14,
                      lineHeight: '20px',
                      color: '#4766FF',
                    }}
                  >
                    Select provider
                  </button>
                </div>
              </div>

              {/* Google Cloud Provider */}
              <div
                className="border border-gray-200 hover:border-purple-500 transition-all rounded-lg cursor-pointer"
                style={{
                  width: '100%',
                  height: 205,
                  borderRadius: 12,
                  display: 'flex',
                  flexDirection: 'column',
                }}
                onClick={() => {
                  setSelectedProvider('GOOGLE');
                  setShowProviderSelection(false);
                  setShowAddForm(true);
                }}
              >
                <div className="flex-1 p-4">
                  <div className="flex items-center mb-3">
                    <div
                      style={{
                        width: 48,
                        height: 48,
                        borderRadius: 8,
                        backgroundColor: '#F0F2FF',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginRight: 12,
                      }}
                    >
                      <img src={googleCloudLogo} alt="Google Cloud" style={{ width: '28px', height: '28px' }} />
                    </div>
                    <span
                      style={{
                        fontWeight: 600,
                        fontSize: 16,
                        lineHeight: '24px',
                        color: '#181D27',
                      }}
                    >
                      Google Cloud
                    </span>
                  </div>
                  <p
                    style={{
                      fontWeight: 400,
                      fontSize: 14,
                      lineHeight: '20px',
                      color: '#535862',
                    }}
                  >
                    Connect to Google AI services including Gemini Pro, Ultra and other models.
                  </p>
                </div>
                <div
                  className="flex items-center justify-end border-t px-4"
                  style={{ height: 53 }}
                >
                  <button
                    className="hover:text-blue-800"
                    style={{
                      fontWeight: 600,
                      fontSize: 14,
                      lineHeight: '20px',
                      color: '#4766FF',
                    }}
                  >
                    Select provider
                  </button>
                </div>
              </div>

              {/* Anthropic Provider */}
              <div
                className="border border-gray-200 hover:border-orange-400 transition-all rounded-lg cursor-pointer"
                style={{
                  width: '100%',
                  height: 205,
                  borderRadius: 12,
                  display: 'flex',
                  flexDirection: 'column',
                }}
                onClick={() => {
                  setSelectedProvider('ANTHROPIC');
                  setShowProviderSelection(false);
                  setShowAddForm(true);
                }}
              >
                <div className="flex-1 p-4">
                  <div className="flex items-center mb-3">
                    <div
                      style={{
                        width: 48,
                        height: 48,
                        borderRadius: 8,
                        backgroundColor: '#FDF2E9',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginRight: 12,
                      }}
                    >
                      <img src={anthropicLogo} alt="Anthropic" style={{ width: '28px', height: '28px' }} />
                    </div>
                    <span
                      style={{
                        fontWeight: 600,
                        fontSize: 16,
                        lineHeight: '24px',
                        color: '#181D27',
                      }}
                    >
                      Anthropic Claude
                    </span>
                  </div>
                  <p
                    style={{
                      fontWeight: 400,
                      fontSize: 14,
                      lineHeight: '20px',
                      color: '#535862',
                    }}
                  >
                    Connect to Anthropic Claude 3.5 Sonnet, Opus and other models.
                  </p>
                </div>
                <div
                  className="flex items-center justify-end border-t px-4"
                  style={{ height: 53 }}
                >
                  <button
                    className="hover:text-blue-800"
                    style={{
                      fontWeight: 600,
                      fontSize: 14,
                      lineHeight: '20px',
                      color: '#4766FF',
                    }}
                  >
                    Select provider
                  </button>
                </div>
              </div>

              {/* AWS Bedrock Provider */}
              <div
                className="border border-gray-200 hover:border-orange-500 transition-all rounded-lg cursor-pointer"
                style={{
                  width: '100%',
                  height: 205,
                  borderRadius: 12,
                  display: 'flex',
                  flexDirection: 'column',
                }}
                onClick={() => {
                  setSelectedProvider('AWS');
                  setShowProviderSelection(false);
                  setShowAddForm(true);
                }}
              >
                <div className="flex-1 p-4">
                  <div className="flex items-center mb-3">
                    <div
                      style={{
                        width: 48,
                        height: 48,
                        borderRadius: 8,
                        backgroundColor: '#FFF7ED',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginRight: 12,
                      }}
                    >
                      <img src={awsLogo} alt="AWS" style={{ width: '28px', height: '28px' }} />
                    </div>
                    <span
                      style={{
                        fontWeight: 600,
                        fontSize: 16,
                        lineHeight: '24px',
                        color: '#181D27',
                      }}
                    >
                      AWS
                    </span>
                  </div>
                  <p
                    style={{
                      fontWeight: 400,
                      fontSize: 14,
                      lineHeight: '20px',
                      color: '#535862',
                    }}
                  >
                    Connect to Amazon Bedrock for Claude, Titan and other models.
                  </p>
                </div>
                <div
                  className="flex items-center justify-end border-t px-4"
                  style={{ height: 53 }}
                >
                  <button
                    className="hover:text-blue-800"
                    style={{
                      fontWeight: 600,
                      fontSize: 14,
                      lineHeight: '20px',
                      color: '#4766FF',
                    }}
                  >
                    Select provider
                  </button>
                </div>
              </div>

              {/* Other Provider */}
              <div 
                className="border border-gray-200 rounded-lg p-4 hover:border-blue-500 hover:shadow-md transition-all"
                style={{
                  width: '100%',
                }}
              >
                <div className="flex items-center mb-3">
                  <div
                    style={{
                      width: 48,
                      height: 48,
                      borderRadius: 8,
                      backgroundColor: '#F3F4F6',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: 8,
                    }}
                  >
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#6B7280"
                      strokeWidth="2"
                    >
                      <path d="M12 2L2 7l10 5 10-5-10-5z" />
                      <path d="M2 17l10 5 10-5" />
                      <path d="M2 12l10 5 10-5" />
                    </svg>
                  </div>
                  <span
                    style={{
                      fontWeight: 600,
                      fontSize: 16,
                      lineHeight: '24px',
                      color: '#181D27',
                    }}
                  >
                    Other
                  </span>
                </div>
                <p
                  style={{ fontWeight: 400, fontSize: 14, lineHeight: '20px', color: '#535862' }}
                  className="mb-4"
                >
                  Connect to custom or other AI provider endpoints not listed above.
                </p>
                <div className="text-right">
                  <button
                    className="hover:text-blue-800"
                    style={{
                      fontWeight: 600,
                      fontSize: 14,
                      lineHeight: '20px',
                      color: '#4766FF',
                    }}
                    onClick={() => {
                      setSelectedProvider('OTHER');
                      setShowProviderSelection(false);
                      setShowAddForm(true);
                    }}
                  >
                    Select provider
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : showAddForm ? (
        /* Add Configuration Form */
        <div className="mt-4">
          <Typography
            style={{
              fontWeight: 600,
              fontSize: '18px',
              color: '#101828',
              marginBottom: '4px',
            }}
          >
            {editingAiLlmId ? 'Edit LLM' : 'Add LLM'}
          </Typography>
          <Typography
            style={{
              fontSize: '14px',
              color: '#667085',
              marginBottom: '24px',
            }}
          >
            {editingAiLlmId 
              ? 'Update your AI language model configuration.'
              : 'Configure your AI language model provider credentials.'}
          </Typography>
          <div className="bg-white p-6 rounded-lg shadow-sm border border-[#E9EAEB]">
            <Typography 
              variant="h6" 
              className="mb-2"
              style={{
                fontWeight: 600,
                fontSize: '18px',
                color: '#181D27',
              }}
            >
              {editingAiLlmId ? 'Edit' : 'Configure'} {selectedProvider} Provider
            </Typography>
            <Typography 
              variant="body2" 
              style={{ color: '#535862', marginBottom: '24px' }}
            >
              {editingAiLlmId 
                ? `Update your ${selectedProvider} credentials and configuration details.`
                : `Enter your ${selectedProvider} credentials and configuration details.`}
            </Typography>

            {/* Selected Provider banner with change action */}
            <div className="mb-6 flex items-center justify-between rounded-md border border-gray-200 bg-gray-50 px-4 py-3">
              <div className="flex items-center gap-3">
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 6,
                    backgroundColor: (() => {
                      const colors = getAITypeColor(selectedProvider);
                      return colors.bg;
                    })(),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={(() => {
                      const colors = getAITypeColor(selectedProvider);
                      return colors.color;
                    })()}
                    strokeWidth="2"
                  >
                    <path d="M12 2L2 7l10 5 10-5-10-5z" />
                    <path d="M2 17l10 5 10-5" />
                    <path d="M2 12l10 5 10-5" />
                  </svg>
                </div>
                <span className="text-sm text-gray-800">
                  Selected provider:
                  <span className="ml-1 font-semibold">
                    {selectedProvider === 'OPENAI' && 'OpenAI'}
                    {selectedProvider === 'GOOGLE' && 'Google'}
                    {selectedProvider === 'ANTHROPIC' && 'Anthropic'}
                    {selectedProvider === 'AWS' && 'AWS'}
                    {selectedProvider === 'OTHER' && 'Other'}
                  </span>
                </span>
              </div>
              {!editingAiLlmId && (
                <button
                  type="button"
                  onClick={() => {
                    setConfigName('');
                    setEndpoint('');
                    setApiKey('');
                    setApiSecret('');
                    setShowAddForm(false);
                    setShowProviderSelection(true);
                  }}
                  className="text-sm font-semibold"
                  style={{ color: '#4766FF' }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.color = '#2E4EE8';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.color = '#4766FF';
                  }}
                >
                  Change provider
                </button>
              )}
            </div>

            <form className="space-y-6">
              {/* Configuration Name */}
              <div>
                <label 
                  htmlFor="config-name" 
                  className="block text-sm font-medium mb-2"
                  style={{ color: '#181D27' }}
                >
                  Configuration Name <span style={{ color: '#DC2626' }}>*</span>
                </label>
                <input
                  id="config-name"
                  type="text"
                  placeholder="e.g., Production OpenAI Config"
                  value={configName}
                  onChange={(e) => setConfigName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  style={{ fontSize: '14px' }}
                  data-tutorial="ai-config-name"
                />
                <p className="mt-1 text-xs" style={{ color: '#6B7280' }}>
                  A human-readable name to identify this configuration
                </p>
              </div>

              {/* API Key Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium mb-2" style={{ color: '#181D27' }}>
                  API Key Mode
                </label>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <input
                      type="radio"
                      id="new-api-key"
                      name="api-key-mode"
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                      checked={!apiKeyUuid}
                      onChange={() => setApiKeyUuid(undefined)}
                    />
                    <label htmlFor="new-api-key" className="ml-3 block text-sm font-medium text-gray-700">
                      Configure new credentials
                    </label>
                  </div>

                  <div>
                    <div className="flex items-center">
                      <input
                        type="radio"
                        id="existing-api-key"
                        name="api-key-mode"
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                        checked={!!apiKeyUuid}
                        disabled={availableApiKeys.length === 0}
                        onChange={() => {
                          const suitableKey = availableApiKeys.find(k => k.provider === selectedProvider) || availableApiKeys[0];
                          if (suitableKey) {
                            setApiKeyUuid(suitableKey.id);
                          }
                        }}
                      />
                      <label
                        htmlFor="existing-api-key"
                        className={`ml-3 block text-sm font-medium ${availableApiKeys.length === 0 ? 'text-gray-400' : 'text-gray-700'}`}
                      >
                        Use existing API key
                        {availableApiKeys.length === 0 && (
                          <span className="font-normal text-gray-400"> (none for this provider)</span>
                        )}
                      </label>
                    </div>

                    {availableApiKeys.length > 0 && apiKeyUuid && (
                      <div className="mt-2 ml-7">
                        <select
                          value={apiKeyUuid}
                          onChange={(e) => setApiKeyUuid(e.target.value)}
                          className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md shadow-sm border"
                        >
                          {availableApiKeys.map((key) => (
                            <option key={key.id} value={key.id}>
                              {key.provider} - {key.key.substring(0, 10)}... (Created {new Date(key.createdAt).toLocaleDateString()})
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {!apiKeyUuid && (
                <>
                  {/* API Endpoint */}
                  <div>
                    <label 
                      htmlFor="endpoint" 
                      className="block text-sm font-medium mb-2"
                      style={{ color: '#181D27', display: 'flex', alignItems: 'center', gap: '6px' }}
                    >
                      API Endpoint <span style={{ color: '#DC2626' }}>*</span>
                      <Tooltip title="The base URL for the AI provider's API. This is where Flashgate will send requests." arrow placement="top">
                        <span style={{ cursor: 'help', display: 'inline-flex', alignItems: 'center' }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"/>
                            <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
                            <line x1="12" y1="17" x2="12.01" y2="17"/>
                          </svg>
                        </span>
                      </Tooltip>
                    </label>
                    <input
                      id="endpoint"
                      type="url"
                      value={endpoint}
                      onChange={(e) => setEndpoint(e.target.value)}
                      placeholder={
                        selectedProvider === 'OPENAI' 
                          ? 'https://api.openai.com/v1'
                          : selectedProvider === 'GOOGLE'
                          ? 'https://generativelanguage.googleapis.com'
                          : selectedProvider === 'ANTHROPIC'
                          ? 'https://api.anthropic.com/v1'
                          : selectedProvider === 'AWS'
                          ? 'https://bedrock-runtime.us-east-1.amazonaws.com'
                          : 'https://your-custom-endpoint.com'
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      style={{ fontSize: '14px' }}
                      required
                    />
                    {selectedProvider === 'OTHER' ? (
                      <p className="mt-1 text-xs" style={{ color: '#6B7280' }}>
                        Enter an endpoint compatible with OpenAI, Anthropic, or other supported AI providers
                      </p>
                    ) : (
                      <p className="mt-1 text-xs" style={{ color: '#6B7280' }}>
                        The base URL for the AI provider's API
                      </p>
                    )}
                  </div>

                  {/* API Key (Optional) */}
                  <div>
                    <label 
                      htmlFor="api-key" 
                      className="block text-sm font-medium mb-2"
                      style={{ color: '#181D27', display: 'flex', alignItems: 'center', gap: '6px' }}
                    >
                      API Key <span style={{ color: '#6B7280', fontWeight: 400 }}>(Optional)</span>
                      <Tooltip title="Optional API key for providers that use key-based authentication instead of or in addition to a secret." arrow placement="top">
                        <span style={{ cursor: 'help', display: 'inline-flex', alignItems: 'center' }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"/>
                            <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
                            <line x1="12" y1="17" x2="12.01" y2="17"/>
                          </svg>
                        </span>
                      </Tooltip>
                    </label>
                    <input
                      id="api-key"
                      type="text"
                      value={apiKey}
                      onChange={(e) => setApiKey(e.target.value)}
                      placeholder="Your API key (if required)"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      style={{ fontSize: '14px' }}
                    />
                    <p className="mt-1 text-xs" style={{ color: '#6B7280' }}>
                      Some providers require both an API key and a secret for authentication
                    </p>
                  </div>

                  {/* API Secret */}
                  <div>
                    <label 
                      htmlFor="api-secret" 
                      className="block text-sm font-medium mb-2"
                      style={{ color: '#181D27' }}
                    >
                      API Secret
                    </label>
                    <input
                      id="api-secret"
                      type="password"
                      value={apiSecret}
                      onChange={(e) => setApiSecret(e.target.value)}
                      placeholder='Your API secret key'
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-500"
                      style={{ fontSize: '14px' }}
                      data-tutorial="ai-api-key"
                    />
                    <p className="mt-1 text-xs" style={{ color: '#6B7280' }}>
                      Secret key or API secret for authentication (encrypted at rest)
                    </p>
                  </div>
                </>
              )}

              {/* Workspace Selection - Always disabled, uses selected workspace */}
              <div>
                <label 
                  htmlFor="workspace" 
                  className="block text-sm font-medium mb-2"
                  style={{ color: '#181D27' }}
                >
                  Workspace
                </label>
                <div
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50"
                  style={{ fontSize: '14px', color: '#535862' }}
                >
                  {globalData.workspaces?.find(w => w.id === selectedWorkspace)?.name || 'Current workspace'}
                </div>
                <p className="mt-1 text-xs" style={{ color: '#6B7280' }}>
                  Configuration will be created in the currently selected workspace
                </p>
              </div>

              {/* Security Notice */}
              <div 
                className="p-4 rounded-lg border"
                style={{ 
                  backgroundColor: '#F0F9FF',
                  borderColor: '#BAE6FD',
                }}
              >
                <div className="flex items-start gap-3">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#0284C7"
                    strokeWidth="2"
                    style={{ flexShrink: 0, marginTop: '2px' }}
                  >
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  <div>
                    <Typography 
                      variant="body2" 
                      style={{ 
                        fontWeight: 600,
                        color: '#0C4A6E',
                        marginBottom: '4px',
                      }}
                    >
                      Security Information
                    </Typography>
                    <Typography 
                      variant="body2" 
                      style={{ 
                        fontSize: '13px',
                        color: '#075985',
                        lineHeight: '1.5',
                      }}
                    >
                      All API keys and secrets are encrypted before storage and never returned in API responses.
                      You can validate the configuration after creation to ensure connectivity.
                    </Typography>
                  </div>
                </div>
              </div>

              {/* Form Actions */}
              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  className="px-4 py-2 rounded-lg font-medium text-white transition-colors"
                  data-tutorial="ai-submit"
                  style={{
                    backgroundColor: '#617BFF',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#5A6FE8';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#617BFF';
                  }}
                  onClick={async (e) => {
                    e.preventDefault();
                    try {
                      if (editingAiLlmId) {
                        // Update existing AI LLM
                        // Note: API secret cannot be changed, so we don't include it in the update
                        const updateData: any = {
                          name: configName,
                          endpoint: apiKeyUuid ? undefined : endpoint,
                          secret: apiKeyUuid ? undefined : apiSecret,
                          key: apiKeyUuid ? undefined : (apiKey || null),
                          apiKeyUuid: apiKeyUuid,
                        };
                        
                        const resp = await withTokenRefresh(() => apiClient.updateAiLlm(editingAiLlmId, updateData));
                        if (resp.success) {
                          toastSuccess('AI LLM configuration updated successfully');
                          // Reset form state
                          setConfigName('');
                          setEndpoint('');
                          setApiKey('');
                          setApiSecret('');
                          setEditingAiLlmId(null);
                          setShowAddForm(false);
                          setShowProviderSelection(false);
                          // Reload the list
                          await loadAILlms();
                        } else {
                          toastError(resp.message || 'Failed to update AI LLM configuration');
                        }
                      } else {
                        // Create new AI LLM
                        const resp = await withTokenRefresh(() => apiClient.createAiLlm({
                          name: configName,
                          aiType: selectedProvider as AiLlmTypes.AiType,
                          endpoint: apiKeyUuid ? undefined : endpoint,
                          key: apiKeyUuid ? undefined : (apiKey || null),
                          secret: apiKeyUuid ? undefined : apiSecret,
                          apiKeyUuid: apiKeyUuid,
                          workspaceId: selectedWorkspace,
                        } as any));
                        if (resp.success) {
                          toastSuccess('AI LLM configuration created successfully');
                          // Reset form state
                          setConfigName('');
                          setEndpoint('');
                          setApiKey('');
                          setApiSecret('');
                          setShowAddForm(false);
                          setShowProviderSelection(false);
                          // Reload the list
                          await loadAILlms();
                          // Notify that AI LLM was created (for tutorial)
                          window.dispatchEvent(new CustomEvent('aiLlmCreated'));
                        } else {
                          toastError(resp.message || 'Failed to create AI LLM configuration');
                        }
                      }
                    } catch (error) {
                      console.error('[AILLM] Error saving AI LLM:', error);
                      toastError(editingAiLlmId ? 'Failed to update AI LLM configuration' : 'Failed to create AI LLM configuration');
                    }
                  }}
                >
                  {editingAiLlmId ? 'Update Configuration' : 'Create Configuration'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : (
        /* AI LLM Configurations Table */
        <div className="bg-white rounded-lg shadow-md mt-8 mb-8 border border-[#E9EAEB]">
        <div style={{ overflow: 'visible' }}>
          <table className="w-full table-auto">
            <thead>
              <tr
                className="border-b border-gray-200"
                style={{ backgroundColor: 'white', height: '44px' }}
              >
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="hidden lg:table-cell px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="hidden lg:table-cell px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tokens In
                </th>
                <th className="hidden lg:table-cell px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tokens Out
                </th>
                <th className="hidden xl:table-cell px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Requests
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center">
                    <Typography style={{ color: '#667085' }}>Loading LLM configurations...</Typography>
                  </td>
                </tr>
              ) : filteredConfigs.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center">
                    <div 
                      className="inline-block p-6 rounded-lg border"
                      style={{ 
                        backgroundColor: '#F9FAFB',
                        borderColor: '#E5E7EB',
                      }}
                    >
                      <Typography style={{ color: '#667085', fontSize: '14px', fontWeight: 600, marginBottom: '8px' }}>
                        {searchQuery ? 'No LLM found' : 'No LLM configuration'}
                      </Typography>
                      <Typography style={{ color: '#9CA3AF', fontSize: '13px' }}>
                        {searchQuery
                          ? 'Try adjusting your search query'
                          : 'Create your first LLM configuration to get started'}
                      </Typography>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredConfigs.map((config) => {
                  const typeStyle = getAITypeColor(config.aiType);
                  // Get stats from API
                  const stats = llmStats[config.id];
                  const tokensIn = parseInt(stats?.totalTokensIn || '0');
                  const tokensOut = parseInt(stats?.totalTokensOut || '0');
                  const calls = parseInt(stats?.totalApiCalls || '0');

                  const formatTokens = (num: number) => {
                    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
                    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
                    return num.toString();
                  };

                  return (
                    <tr
                      key={config.id}
                      className="hover:bg-gray-50 transition-all cursor-pointer"
                      style={{ height: '72px' }}
                      onClick={() => navigate(`/ai/llm/${config.id}`)}
                    >
                      {/* Name with Provider Logo */}
                      <td className="px-4 py-4 truncate">
                        <div className="flex items-center space-x-3">
                          <div className="flex-shrink-0">
                            <div
                              className="flex items-center justify-center"
                              style={{
                                width: '32px',
                                height: '32px',
                                borderRadius: '200px',
                                backgroundColor: '#FFFFFF',
                                border: '0.5px solid #E5E7EB',
                              }}
                            >
                              {getProviderIcon(config.aiType)}
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <Typography
                              variant="body1"
                              style={{ color: '#181D27', fontWeight: 600, fontSize: '14px' }}
                              className="truncate"
                              title={config.name}
                            >
                              {config.name}
                            </Typography>
                          </div>
                        </div>
                      </td>
                      {/* Created Date */}
                      <td className="hidden lg:table-cell px-4 py-4 truncate">
                        <Typography variant="body2" style={{ color: '#717680', fontSize: '13px' }}>
                          {formatDate(config.createdAt)}
                        </Typography>
                      </td>
                      {/* Tokens In */}
                      <td className="hidden lg:table-cell px-4 py-4 text-right">
                        <Typography variant="body2" style={{ color: '#181D27', fontSize: '13px', fontWeight: 500 }}>
                          {formatTokens(tokensIn)}
                        </Typography>
                      </td>
                      {/* Tokens Out */}
                      <td className="hidden lg:table-cell px-4 py-4 text-right">
                        <Typography variant="body2" style={{ color: '#181D27', fontSize: '13px', fontWeight: 500 }}>
                          {formatTokens(tokensOut)}
                        </Typography>
                      </td>
                      {/* Calls */}
                      <td className="hidden xl:table-cell px-4 py-4 text-right">
                        <Typography variant="body2" style={{ color: '#181D27', fontSize: '13px', fontWeight: 500 }}>
                          {formatTokens(calls)}
                        </Typography>
                      </td>
                      {/* Actions - 3-dot menu only */}
                      <td className="px-4 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                        <IconButton
                          onClick={(e) => { e.stopPropagation(); handleMenuOpen(e, config.id); }}
                          size="small"
                          sx={{
                            color: '#6B7280',
                            '&:hover': { backgroundColor: '#F3F4F6' },
                          }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        </div>
      )}

          </div>
        </div>
      </div>

      {/* Actions Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            minWidth: '160px',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            border: '1px solid #E5E7EB',
          },
        }}
      >
        <MenuItem
          onClick={() => {
            if (menuConfigId) navigate(`/ai/llm/${menuConfigId}`);
            handleMenuClose();
          }}
          sx={{ fontSize: '14px', fontWeight: 600, color: '#414651', py: 1.5 }}
        >
          <img src={eyeSvg} alt="View" style={{ width: '18px', height: '18px', marginRight: '12px' }} />
          View
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuConfigId) {
              const config = aiLlms.find(c => c.id === menuConfigId);
              if (config) {
                setAiLlmToDelete(config);
                setDeleteDialogOpen(true);
              }
            }
            handleMenuClose();
          }}
          sx={{ fontSize: '14px', fontWeight: 600, color: '#F04438', py: 1.5 }}
        >
          <img src={deleteSvg} alt="Delete" style={{ width: '18px', height: '18px', marginRight: '12px' }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete AI LLM Configuration</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the AI LLM configuration "{aiLlmToDelete?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={async () => {
              if (!aiLlmToDelete) return;
              
              try {
                const resp = await withTokenRefresh(() => apiClient.deleteAiLlm(aiLlmToDelete.id));
                if (resp.success) {
                  toastSuccess('AI LLM configuration deleted successfully');
                  setDeleteDialogOpen(false);
                  setAiLlmToDelete(null);
                  // Reload the list
                  await loadAILlms();
                } else {
                  toastError(resp.message || 'Failed to delete AI LLM configuration');
                }
              } catch (error) {
                console.error('[AILLM] Error deleting AI LLM:', error);
                toastError('Failed to delete AI LLM configuration');
              }
            }}
            style={{ color: '#DC2626' }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Guided Highlight - non-intrusive tutorial */}
      <GuidedHighlight
        steps={aiLlmGuideSteps}
        isActive={showGuide && !showProviderSelection && !showAddForm}
        onComplete={handleGuideComplete}
        onDismiss={handleGuideDismiss}
        currentStep={guideStep}
        onStepChange={setGuideStep}
      />
    </div>
  );
}

export default AILLM;
